import { MARK_PATH, MARK_VIEWBOX } from './logoPath';

type LogoVariant = 'mark' | 'lockup' | 'stacked';

interface LogoProps {
  variant?: LogoVariant;
  /** Height of the mark in px. Text scales from this. */
  size?: number;
  className?: string;
  /** Accessible name. Set to '' for decorative use alongside visible text. */
  title?: string;
}

/**
 * The Falcon mark.
 *
 * Renders MARK_PATH and nothing else — every variant, and every other use of
 * the logo on the site (favicon, watermark, OG image), draws that same single
 * path. Uses differ only in scale, color and opacity, so the mark cannot drift
 * out of sync with itself.
 *
 * Color comes from `currentColor`, so the mark inherits its context: brand blue
 * on light surfaces, white on navy, any opacity for watermarks. There is no
 * white background — the source JPEG's baked-in white field is exactly what
 * this component exists to eliminate.
 */
function Mark({ size, className }: { size: number; className?: string }) {
  const [, , vbW, vbH] = MARK_VIEWBOX.split(' ').map(Number);
  return (
    <svg
      viewBox={MARK_VIEWBOX}
      width={(size * vbW) / vbH}
      height={size}
      className={className}
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d={MARK_PATH} />
    </svg>
  );
}

export function Logo({ variant = 'lockup', size = 32, className = '', title = 'Falcon Solutions' }: LogoProps) {
  const label = title || undefined;

  if (variant === 'mark') {
    return (
      <span className={`inline-flex items-center ${className}`} role={label ? 'img' : undefined} aria-label={label}>
        <Mark size={size} />
      </span>
    );
  }

  if (variant === 'stacked') {
    return (
      <span
        className={`inline-flex flex-col items-center gap-3 ${className}`}
        role={label ? 'img' : undefined}
        aria-label={label}
      >
        <Mark size={size} />
        <span className="flex flex-col items-center leading-none">
          <span
            className="font-display font-extrabold tracking-tight"
            style={{ fontSize: size * 0.5, fontStretch: '88%' }}
          >
            Falcon Solutions
          </span>
          <span
            className="font-sans font-medium opacity-55"
            style={{ fontSize: Math.max(9, size * 0.2), letterSpacing: '0.08em', marginTop: size * 0.12 }}
          >
            (Pvt) Ltd
          </span>
        </span>
      </span>
    );
  }

  // lockup: mark + "Falcon Solutions" over "(Pvt) Ltd"
  //
  // The suffix is small, so it must never render below ~9px. Callers swap to
  // variant="mark" on small screens rather than shrinking this.
  //
  // Tracking is 0.08em, not the 0.18em used when this read "PRIVATE LIMITED":
  // wide letter-spacing suits all-caps, but on mixed-case text it reads as
  // broken word spacing rather than as a considered detail.
  const suffixSize = Math.max(9, size * 0.2);
  return (
    <span
      className={`inline-flex items-center gap-2.5 ${className}`}
      role={label ? 'img' : undefined}
      aria-label={label}
    >
      <Mark size={size} />
      <span className="logo-word flex flex-col leading-none">
        {/*
          0.58 of the mark, not 0.52. The wordmark is what identifies the
          company at a glance — at the smaller ratio the name read as a caption
          beside the bird rather than as the logo itself.
        */}
        <span
          className="font-display font-extrabold tracking-tight whitespace-nowrap"
          style={{ fontSize: size * 0.58, fontStretch: '88%' }}
        >
          Falcon Solutions
        </span>
        <span
          className="font-sans font-medium whitespace-nowrap opacity-55"
          style={{ fontSize: suffixSize, letterSpacing: '0.08em', marginTop: size * 0.1 }}
        >
          (Pvt) Ltd
        </span>
      </span>
    </span>
  );
}
